import { useState, useEffect } from "react";
import { useApi } from "../hooks/useApi";
import { theme, fonts } from "../theme";
import { Card, CardHeader, StatusBadge, Button, Pill, AccentStripe, SectionTitle, ErrorState } from "../components/ui";
import { toast, showModal, SkeletonCards } from "../components/shared";
import RunnerStatusWidget from "../components/RunnerStatusWidget";
import { API_BASE } from "../config";

const profileColors = {
  KONSERVATIV: { bg: "rgba(34,197,94,0.10)",  text: "#16a34a", border: "rgba(34,197,94,0.3)", solid: "#16a34a" },
  MODERAT:     { bg: "rgba(59,130,246,0.10)", text: "#3b82f6", border: "rgba(59,130,246,0.3)", solid: "#3b82f6" },
  AGGRESSIV:   { bg: "rgba(239,68,68,0.10)",  text: "#ef4444", border: "rgba(239,68,68,0.3)", solid: "#ef4444" },
};

const profileExpectations = {
  KONSERVATIV: { yearReturn: "~26%", maxDD: "~$4-5k", sharpe: "2.83", note: "Setup-and-forget, mentale Ruhe" },
  MODERAT:     { yearReturn: "~32-34%", maxDD: "~$6-7k", sharpe: "2.73", note: "Sweet-Spot, 12/13 Quartale profitabel" },
  AGGRESSIV:   { yearReturn: "~45-50%", maxDD: "~$12-13k (25% Capital!)", sharpe: "2.44", note: "Erfahrene Trader, mentaler Magen für 8% DD" },
};

export default function BotControl() {
  const { data: status, error, loading } = useApi("/status", 3000);
  const { data: systemServices } = useApi("/system/services", 5000);
  const [activeProfiles, setActiveProfiles] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [symbolLocks, setSymbolLocks] = useState({});
  const [switching, setSwitching] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/control/active-profile`)
      .then(r => r.json())
      .then(d => {
        setActiveProfiles(d.active_profiles || []);
        setCandidates(d.candidates || []);
        setSymbolLocks(d.symbol_locks || {});
      })
      .catch(() => {});
  }, [status]);

  if (loading) return <SkeletonCards n={3} />;
  if (error) return <ErrorState error={error} />;
  if (!status) return null;

  const positions = status.positions || {};
  const hasOpenPosition = Object.keys(positions).length > 0;
  const anyActive = activeProfiles.length > 0;

  // Helper: gibt es bereits eine live-Strategy auf gleichem Symbol?
  const sameSymbolLive = (target) => {
    return activeProfiles.filter(p =>
      p.symbol === target.symbol && p.key !== target.key
    );
  };

  // Activate (N-of-N)
  const activateProfile = (target) => {
    if (target.is_active) {
      toast(`${target.profile} ist bereits aktiv`, "info");
      return;
    }
    if (hasOpenPosition) {
      toast(`Erst offene Positionen schließen (${Object.keys(positions).join(", ")})`, "error");
      return;
    }

    const exp = profileExpectations[target.profile];
    const sameSymbol = sameSymbolLive(target);
    const symbolWarning = sameSymbol.length > 0
      ? `\n\u26A0 Symbol-Konflikt-Hinweis:
  ${sameSymbol.length} Strategy(s) auf ${target.symbol} bereits live: ${sameSymbol.map(s => s.profile).join(", ")}.
  Symbol-Lock garantiert dass nur eine zur Zeit handelt — andere Signals werden geblockt.
  Wer zuerst ein Setup hat, gewinnt.\n`
      : "";

    showModal(
      `${target.profile} aktivieren?`,
      `Du aktivierst ${target.profile} (${target.risk_pct}% / ${target.max_contracts}\u00D7 / ${target.symbol}).

Erwartete Live-Performance:
  Jahresrendite:  ${exp.yearReturn}
  Max Drawdown:   ${exp.maxDD}
  Sharpe:         ${exp.sharpe}
  Charakter:      ${exp.note}
${symbolWarning}
\u26A0 Bot wird neu gestartet (~5-10 Sekunden offline).
\u26A0 Andere live-Strategies bleiben unverändert.

Fortfahren?`,
      async () => {
        setSwitching(true);
        try {
          const r1 = await fetch(`${API_BASE}/control/activate/${target.key}`, { method: "POST" });
          const d1 = await r1.json();
          if (!r1.ok) {
            toast(`Activation fehlgeschlagen: ${d1.detail || "unknown"}`, "error");
            setSwitching(false);
            return;
          }
          toast(`${target.profile} aktiviert. Bot wird neu gestartet...`, "warn");

          setRestarting(true);
          setTimeout(async () => {
            try {
              await fetch(`${API_BASE}/control/restart`, { method: "POST" });
              toast("Restart triggered. Frontend lädt in 12s neu...", "info");
              setTimeout(() => window.location.reload(), 12000);
            } catch (e) {
              toast(`Restart-Trigger Fehler: ${e.message}`, "error");
              setSwitching(false);
              setRestarting(false);
            }
          }, 800);
        } catch (e) {
          toast("Activation-Error: " + e.message, "error");
          setSwitching(false);
        }
      }
    );
  };

  // Deactivate single strategy
  const deactivateSingle = (target) => {
    if (positions[target.key]) {
      toast(`${target.profile} hat eine offene Position — erst schließen`, "error");
      return;
    }

    showModal(
      `${target.profile} deaktivieren?`,
      `Strategy: ${target.profile} (${target.risk_pct}% / ${target.max_contracts}\u00D7 / ${target.symbol})

Nach Deaktivierung:
  \u2022 Diese Strategy empfängt keine Signals mehr
  \u2022 IBKR-Adapter wird beim Restart NICHT erstellt
  \u2022 Andere live-Strategies bleiben unverändert
  
Verbleibende live-Strategies: ${activeProfiles.length - 1}

\u26A0 Bot wird neu gestartet (~5-10 Sekunden offline).

Fortfahren?`,
      async () => {
        setSwitching(true);
        try {
          const r1 = await fetch(`${API_BASE}/control/deactivate/${target.key}`, { method: "POST" });
          const d1 = await r1.json();
          if (!r1.ok) {
            toast(`Deactivation fehlgeschlagen: ${d1.detail || "unknown"}`, "error");
            setSwitching(false);
            return;
          }
          toast(`${target.profile} deaktiviert. Bot wird neu gestartet...`, "warn");

          setRestarting(true);
          setTimeout(async () => {
            try {
              await fetch(`${API_BASE}/control/restart`, { method: "POST" });
              toast("Restart triggered. Frontend lädt in 12s neu...", "info");
              setTimeout(() => window.location.reload(), 12000);
            } catch (e) {
              toast(`Restart-Trigger Fehler: ${e.message}`, "error");
              setSwitching(false);
              setRestarting(false);
            }
          }, 800);
        } catch (e) {
          toast("Deactivation-Error: " + e.message, "error");
          setSwitching(false);
        }
      }
    );
  };

  // Deactivate ALL
  const deactivateAll = () => {
    if (activeProfiles.length === 0) {
      toast("Keine Strategie ist aktiv", "info");
      return;
    }
    if (hasOpenPosition) {
      toast(`Erst offene Positionen schließen (${Object.keys(positions).join(", ")})`, "error");
      return;
    }

    showModal(
      `Alle ${activeProfiles.length} Strategien deaktivieren?`,
      `Aktuell aktiv:
${activeProfiles.map(p => `  \u2022 ${p.profile} (${p.risk_pct}% / ${p.max_contracts}\u00D7 / ${p.symbol})`).join("\n")}

Nach Deaktivierung:
  \u2022 Keine Strategie wird mehr Signals empfangen
  \u2022 Bot l\u00E4uft im "dashboard-only mode"
  \u2022 IBKR-Adapter werden NICHT erstellt (keine Trades m\u00F6glich)

\u26A0 Bot wird neu gestartet (~5-10 Sekunden offline).
\u26A0 Sicher f\u00FCr Pausen, Wartung, Nicht-Trading-Phasen.

Fortfahren?`,
      async () => {
        setSwitching(true);
        try {
          const r1 = await fetch(`${API_BASE}/control/deactivate-all`, { method: "POST" });
          const d1 = await r1.json();
          if (!r1.ok) {
            toast(`Deactivation fehlgeschlagen: ${d1.detail || "unknown"}`, "error");
            setSwitching(false);
            return;
          }
          toast("Alle Strategien deaktiviert. Bot wird neu gestartet...", "warn");

          setRestarting(true);
          setTimeout(async () => {
            try {
              await fetch(`${API_BASE}/control/restart`, { method: "POST" });
              toast("Restart triggered. Frontend lädt in 12s neu...", "info");
              setTimeout(() => window.location.reload(), 12000);
            } catch (e) {
              toast(`Restart-Trigger Fehler: ${e.message}`, "error");
              setSwitching(false);
              setRestarting(false);
            }
          }, 800);
        } catch (e) {
          toast("Deactivation-Error: " + e.message, "error");
          setSwitching(false);
        }
      }
    );
  };

  const flattenStrategy = (name) => {
    showModal(
      `Position glattstellen: ${name}?`,
      `Sofort die offene Position bei ${name} schlie\u00DFen via IBKR. Diese Aktion kann nicht r\u00FCckg\u00E4ngig gemacht werden.`,
      async () => {
        try {
          const r = await fetch(`${API_BASE}/control/flatten/${name}`, { method: "POST" });
          const d = await r.json();
          if (r.ok) toast(`Flatten-Order gesendet f\u00FCr ${name}`, "warn");
          else toast(`Fehler: ${d.detail || "unknown"}`, "error");
        } catch (e) { toast("Error: " + e.message, "error"); }
      }
    );
  };

  const cancelAllOrders = async (name) => {
    try {
      const r = await fetch(`${API_BASE}/control/cancel-all/${name}`, { method: "POST" });
      if (r.ok) toast(`Alle Orders f\u00FCr ${name} cancelled`, "info");
      else toast("Cancel fehlgeschlagen", "error");
    } catch (e) { toast("Error: " + e.message, "error"); }
  };

  const emergencyFlatten = () => {
    showModal(
      "Emergency Flatten All",
      "ALLE Positionen bei IBKR sofort glattstellen? Diese Aktion kann nicht r\u00FCckg\u00E4ngig gemacht werden.",
      async () => {
        await fetch(`${API_BASE}/control/flatten-all`, { method: "POST" });
        toast("Flatten-All gesendet", "warn");
      }
    );
  };

  return (
    <>
      <SectionTitle>Bot Management</SectionTitle>

      {/* === RUNNER STATUS WIDGET (Sprint 2 Phase D) === */}
      {/* Live-Pipeline-Health der LiveStrategyRunners — pollt /api/runners/status */}
      <RunnerStatusWidget />

      {/* === ACTIVE PROFILES PANEL === */}
      <Card style={{ marginBottom: 18 }}>
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "1px", marginBottom: 6 }}>
                Aktive Profile {anyActive && `(${activeProfiles.length})`}
              </div>
              {anyActive ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {activeProfiles.map(p => {
                    const pc = profileColors[p.profile];
                    const lockedBy = symbolLocks[p.symbol]?.holder;
                    const isHolder = lockedBy === p.key;
                    return (
                      <div key={p.key} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 10px",
                        borderRadius: 8,
                        background: pc?.bg,
                        border: `1px solid ${pc?.border}`,
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: pc?.text, fontFamily: fonts.mono, letterSpacing: "0.5px" }}>{p.profile}</span>
                        <span style={{ fontSize: 11, color: theme.text, fontFamily: fonts.mono }}>
                          {p.risk_pct}% / {p.max_contracts}{"\u00D7"} / {p.symbol}
                        </span>
                        {isHolder && (
                          <span title="Symbol-Lock gehalten — diese Strategy ist gerade im Trade" style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: 4,
                            background: "rgba(245,158,11,0.15)",
                            color: "#d97706",
                          }}>{"\uD83D\uDD12"} LOCK</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: theme.text3 }}>Keine Strategie live</span>
                  <Pill bg={theme.tint} color={theme.text3}>dashboard-only</Pill>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {hasOpenPosition && (
                <Pill bg={theme.amberLight || "rgba(245,158,11,0.10)"} color={theme.amberText || "#d97706"}>
                  {Object.keys(positions).length} offene Position(en)
                </Pill>
              )}
              {anyActive && !hasOpenPosition && (
                <Button danger onClick={deactivateAll} disabled={switching}>
                  {"\u23FB"} Alle Deaktivieren
                </Button>
              )}
            </div>
          </div>

          {(switching || restarting) && (
            <div style={{ padding: "12px 14px", background: theme.blueLight, borderRadius: 8, marginBottom: 14, fontSize: 12, color: theme.blueText, fontFamily: fonts.mono }}>
              {restarting ? "\u23F3 Bot wird neu gestartet \u2014 Reload in K\u00FCrze..." : "\u23F3 Aktion l\u00E4uft..."}
            </div>
          )}

          {/* === PROFILE SWITCHER === */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
              Profil aktivieren / deaktivieren
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(candidates.length, 1)}, 1fr)`, gap: 10 }}>
              {candidates.length === 0 && (
                <div style={{ padding: 20, color: theme.text3, fontSize: 12, textAlign: "center" }}>
                  Keine v8.0-Profile geladen.
                </div>
              )}
              {candidates.map((c) => {
                const pc = profileColors[c.profile];
                const exp = profileExpectations[c.profile];
                const sameSymbolOthers = activeProfiles.filter(p => p.symbol === c.symbol && p.key !== c.key);
                return (
                  <div
                    key={c.key}
                    style={{
                      padding: "14px 16px",
                      borderRadius: 10,
                      border: `2px solid ${c.is_active ? pc?.solid : theme.divider}`,
                      background: c.is_active ? pc?.bg : "transparent",
                      cursor: switching ? "default" : "pointer",
                      transition: "all 0.15s",
                      opacity: switching ? 0.5 : 1,
                    }}
                    onClick={() => {
                      if (switching) return;
                      if (c.is_active) deactivateSingle(c);
                      else activateProfile(c);
                    }}
                    onMouseEnter={(e) => {
                      if (!switching) e.currentTarget.style.borderColor = pc?.solid;
                    }}
                    onMouseLeave={(e) => {
                      if (!switching && !c.is_active) e.currentTarget.style.borderColor = theme.divider;
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{
                        padding: "3px 9px",
                        borderRadius: 6,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "0.5px",
                        background: pc?.bg,
                        color: pc?.text,
                        border: `1px solid ${pc?.border}`,
                        fontFamily: fonts.mono,
                      }}>{c.profile}</span>
                      {c.is_active && <Pill bg={pc?.bg} color={pc?.text}>AKTIV</Pill>}
                    </div>
                    <div style={{ fontSize: 13, fontFamily: fonts.mono, fontWeight: 700, color: theme.text, marginBottom: 4 }}>
                      {c.risk_pct}% / {c.max_contracts}{"\u00D7"}
                    </div>
                    <div style={{ fontSize: 10, color: theme.text4, marginBottom: 6, fontFamily: fonts.mono }}>{c.symbol}</div>
                    <div style={{ fontSize: 10, color: theme.text3, lineHeight: 1.5 }}>
                      Return: <strong>{exp?.yearReturn}</strong><br/>
                      Max DD: {exp?.maxDD}<br/>
                      Sharpe: {exp?.sharpe}
                    </div>
                    {sameSymbolOthers.length > 0 && !c.is_active && (
                      <div style={{ fontSize: 9, color: "#d97706", marginTop: 6, padding: "4px 6px", background: "rgba(245,158,11,0.10)", borderRadius: 4 }}>
                        {"\u26A0"} {sameSymbolOthers.length} weitere Strategy auf {c.symbol} live (Symbol-Lock greift)
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: theme.text4, marginTop: 8, fontStyle: "italic" }}>
              {"\u2139"} Klick auf inaktive Karte aktiviert, Klick auf aktive Karte deaktiviert.
            </div>
          </div>
        </div>
      </Card>

      {/* === STRATEGIES (read-only Übersicht + Per-Strategy-Actions) === */}
      <SectionTitle>Strategien</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(candidates.length, 3)}, 1fr)`, gap: 16, marginBottom: 28 }}>
        {candidates.map((c) => {
          const pos = positions[c.key];
          const pc = profileColors[c.profile];
          const isLive = c.is_active;
          const lockedBy = symbolLocks[c.symbol]?.holder;
          const isHolder = lockedBy === c.key;
          return (
            <Card key={c.key}>
              <AccentStripe positive={isLive} />
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: pc?.bg, border: `1px solid ${pc?.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.mono, fontWeight: 800, fontSize: 14, color: pc?.text }}>
                      {c.symbol || "MGC"}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>{c.profile}</div>
                      <div style={{ fontSize: 10, color: theme.text3, fontFamily: fonts.mono }}>{c.filename}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    <StatusBadge status={isLive ? "live" : "test"} />
                    {isHolder && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(245,158,11,0.15)", color: "#d97706", fontFamily: fonts.mono }}>
                        {"\uD83D\uDD12"} LOCK
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                  {[
                    ["Risk/Trade", `${c.risk_pct}%`],
                    ["Max Cap", `${c.max_contracts}\u00D7`],
                    ["Capital", `$${(c.initial_capital/1000).toFixed(0)}k`],
                    ["Position", pos ? `${pos.direction} ${pos.qty || pos.original_qty}` : "Flat"],
                  ].map(([k, v], j) => (
                    <div key={j}>
                      <div style={{ fontSize: 9, fontWeight: 600, color: theme.text4, textTransform: "uppercase", letterSpacing: ".7px", marginBottom: 3 }}>{k}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: fonts.mono, color: pos && k === "Position" ? theme.blueText : theme.text }}>{v}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 8, paddingTop: 16, borderTop: `1px solid ${theme.divider}`, flexWrap: "wrap" }}>
                  {isLive ? (
                    <>
                      {pos && <Button danger onClick={() => flattenStrategy(c.key)}>Flatten</Button>}
                      <Button onClick={() => cancelAllOrders(c.key)}>Cancel Orders</Button>
                      <Button warning onClick={() => deactivateSingle(c)} disabled={switching || pos}>
                        {"\u23FB"} Deaktivieren
                      </Button>
                    </>
                  ) : (
                    <Button primary onClick={() => activateProfile(c)} disabled={switching || hasOpenPosition}>
                      Aktivieren
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* === EMERGENCY === */}
      <Card>
        <CardHeader title="Emergency Controls" sub="Use with caution" />
        <div style={{ padding: "16px 24px", display: "flex", gap: 12 }}>
          <Button danger onClick={emergencyFlatten}>{"\u26A0"} Flatten All Positions</Button>
          <Button onClick={() => fetch(`${API_BASE}/reconciliation/check`, { method: "POST" })}>Force Reconciliation</Button>
        </div>
      </Card>

      {/* === SYSTEM SERVICES === */}
      <Card style={{ marginTop: 16 }}>
        <CardHeader title="System Services" sub={`${status.host || "178.104.155.90"}`} />
        <div style={{ padding: "16px 24px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {(systemServices?.services || [
            // Fallback solange API noch lädt — alle als Loading dargestellt
            { name: "trading-bot", active: false, status: "loading" },
            { name: "ib-gateway-live", active: false, status: "loading" },
            { name: "ib-gateway", active: false, status: "loading" },
          ]).map((svc) => ({ ...svc, ok: svc.active })).map((svc) => (
            <div key={svc.name} className="sl-card" style={{ padding: 16, borderRadius: 12, background: svc.ok ? theme.tint : theme.redLight }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, fontFamily: fonts.mono }}>{svc.name}</span>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: svc.ok ? theme.green : theme.red, animation: svc.ok ? "liveDot 2.5s infinite" : "none" }} />
              </div>
              <div style={{ fontSize: 11, color: svc.ok ? theme.text3 : theme.redText, fontWeight: svc.ok ? 400 : 600 }}>{svc.ok ? "Active" : (svc.status === "loading" ? "..." : (svc.status || "Down"))}</div>
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}
